import { useEffect, useState } from 'react';

import { SearchResult } from '@/components/shared/components/SuperSearchBar/types/SearchResult';
import { useConfigContext } from '@/contexts/ConfigContext/ConfigContext';

interface UseKeyboard {
  setOpened: (opened: boolean) => void;
  searchResults?: SearchResult[];
  paginationProps: {
    page: number;
    itemCount: number;
    handlePageChange: (event: any, newPage: any) => void;
    pageSize: number;
    handlePageSizeChange: (event: any) => void;
  };
}

export const useKeyboard = ({ setOpened, searchResults, paginationProps }: UseKeyboard) => {
  const {
    state: { platform },
  } = useConfigContext();
  const { handlePageChange, page, itemCount, pageSize } = paginationProps;
  const [selectedResult, setSelectedResult] = useState(0);
  const [doNavigate, setDoNavigate] = useState<boolean>();

  // Open and close the search bar

  useEffect(() => {
    if (!platform) return;
    function handleKeySearch(event: KeyboardEvent) {
      if (!platform) return;
      if (platform === 'mac') {
        if (event.metaKey && event.key === 'k') setOpened(true);
      } else {
        if (event.ctrlKey && event.shiftKey && event.keyCode === 75) setOpened(true);
      }
    }
    document.addEventListener('keydown', handleKeySearch);
    return () => {
      document.removeEventListener('keydown', handleKeySearch);
    };
  }, [platform]);

  // Move up and down between results

  useEffect(() => {
    if (!searchResults) return;
    setSelectedResult(0);
  }, [searchResults]);

  useEffect(() => {
    if (!searchResults?.length) return;
    function handleKeyUpOrDown(event: KeyboardEvent) {
      if (!searchResults?.length) return;
      if (event.key === 'ArrowUp' && selectedResult > 0) {
        setSelectedResult(selectedResult - 1);
      } else if (event.key === 'ArrowDown' && selectedResult < searchResults.length - 1) {
        setSelectedResult(selectedResult + 1);
      }
    }
    window.addEventListener('keydown', handleKeyUpOrDown);
    return () => {
      window.removeEventListener('keydown', handleKeyUpOrDown);
    };
  }, [searchResults, selectedResult]);

  useEffect(() => {}, [selectedResult]);

  // Handle navigation on Enter

  useEffect(() => {
    function handleKeyEnter(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        setDoNavigate(true);
      }
    }
    window.addEventListener('keydown', handleKeyEnter);
    return () => {
      window.removeEventListener('keydown', handleKeyEnter);
    };
  }, []);

  // Handle pagination navigation with Option + Arrow keys

  useEffect(() => {
    function handlePaginationNavigation(event: KeyboardEvent) {
      const totalPages = Math.ceil(itemCount / pageSize);
      const canGoPrevious = page > 0;
      const canGoNext = page < totalPages - 1;

      if (event.altKey && event.key === 'ArrowLeft' && canGoPrevious) {
        event.preventDefault();
        handlePageChange(null, page - 1);
      } else if (event.altKey && event.key === 'ArrowRight' && canGoNext) {
        event.preventDefault();
        handlePageChange(null, page + 1);
      }
    }
    window.addEventListener('keydown', handlePaginationNavigation);
    return () => {
      window.removeEventListener('keydown', handlePaginationNavigation);
    };
  }, [handlePageChange, page, itemCount, pageSize]);

  return { selectedResult, onNavigate: doNavigate, platform };
};
